/**
 * Единый гард разделов (DEV-1253, ADR-0042).
 *
 * Заменяет собой три несовместимых механизма, живших до реформы: ролевые
 * beforeEnter, список запрещённых префиксов для маркетолога и whitelist
 * менеджера каталога. Все трое описывали доступ разными словами и расходились
 * с тем, что реально отдаёт бэкенд.
 *
 * Здесь решается ровно один вопрос — «рисовать ли экран». Доступ к данным
 * решает право на RPC внутри сервиса; гард его не заменяет и не дублирует.
 */
import type { NavigationGuardNext, RouteLocationNormalized } from 'vue-router'

import { useAuthStore } from '@/features/auth/stores/auth.store'
import { useSectionsStore } from '@/features/auth/stores/sections.store'

/** Экран отказа для допущенного в приложение: внутри layout, с сайдбаром. */
export const FORBIDDEN_PATH = '/dashboard/forbidden'

/** Экран отказа для недопущенного: без layout, сайдбар рисовать нечем. */
export const NO_ACCESS_PATH = '/no-access'

const isDashboardPath = (path: string): boolean =>
  path === '/dashboard' || path.startsWith('/dashboard/')

export const sectionGuard = async (
  to: RouteLocationNormalized,
  from: RouteLocationNormalized,
  next: NavigationGuardNext,
): Promise<void> => {
  // Логин, восстановление пароля и экран «нет доступа» живут вне дашборда.
  // Гейтить их нечем и незачем; NO_ACCESS_PATH к тому же зациклился бы сам на
  // себе, потому что попадают на него именно с пустой картой.
  if (!isDashboardPath(to.path)) {
    next()
    return
  }

  const authStore = useAuthStore()

  if (!authStore.isAuthenticated) {
    next({ path: '/login', query: { redirect: to.fullPath } })
    return
  }

  const sections = useSectionsStore()

  try {
    // Навигация ждёт карту. Решение на промежуточном состоянии — это ровно та
    // гонка, из-за которой сайдбар успевал отрисоваться по старым правам.
    await sections.ensureLoaded()
  } catch {
    // Сеть отвалилась — это не отказ в доступе. Запирать человека на белом
    // экране из-за таймаута нельзя: данные всё равно защищает бэкенд, а стор
    // остался в статусе error и повторит попытку на следующем переходе.
    next()
    return
  }

  // Ни одного раздела при живом ответе сервера — принципал не принадлежит
  // приложению (агент в CRM, партнёр, пользователь без ролей). Внутрь его не
  // пускаем вообще: сайдбар был бы пустым, а дашборд — набором отказов.
  if (sections.status === 'ready' && sections.keys.length === 0) {
    if (to.path === NO_ACCESS_PATH) {
      next()
      return
    }

    next(NO_ACCESS_PATH)
    return
  }

  const section = to.meta.section

  // Fail-closed: маршрут без раздела недоступен. Забытый meta.section — это
  // дефект, и он обязан выглядеть как дефект, а не как «всем можно».
  // Против забывчивости стоит routeCoverage.spec.ts, здесь — последний рубеж.
  if (section === undefined) {
    // Сам экран отказа meta.section не носит только по недосмотру — но если
    // это случится, зацикливать его на себе нельзя.
    if (to.path === FORBIDDEN_PATH) {
      next()
      return
    }

    next(FORBIDDEN_PATH)
    return
  }

  if (section === 'self' || sections.hasSection(section)) {
    next()
    return
  }

  // Ключ раздела едет на экран отказа: он называет раздел по-человечески и
  // показывает ключ администратору, чтобы тот не выяснял его перепиской.
  next({ path: FORBIDDEN_PATH, query: { section } })
}
